import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Eye, Loader2, CheckCircle2, MessageCircle, Share2, Printer, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, daysUntil, fmtDate } from "@/lib/api";
import { uploadFile } from "@/lib/compress";
import { DocPreview } from "@/components/docs";
import { useAuth } from "@/context/AuthContext";

export const VENDOR_CATS = ["Individual Owner", "Fleet Transporter", "Commission Agent"];
export const KYC_TYPES = ["Gumasta License", "Aadhaar Document"];
export const PAY_MODES = ["Cash", "UPI", "Diesel Voucher", "Bank Transfer", "Cheque"];
export const MARKET_DOCS = [
  ["rc", "RC (Registration Certificate)"],
  ["insurance", "Insurance Policy"],
  ["fitness", "Fitness Certificate"],
  ["puc", "PUC Certificate"],
  ["permit", "Permit (National / State)"],
  ["dl", "Driver Driving License (DL)"],
];
export const TRIP_STATUS_STYLE = {
  Active: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800",
  "POD Verified": "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400 dark:border-indigo-800",
  Settled: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
};

export function useMarketRoles() {
  const { role } = useAuth();
  return {
    role,
    canFin: ["admin", "manager", "accounts"].includes(role),
    canOps: ["admin", "manager", "operation"].includes(role),
    isAdmin: role === "admin",
  };
}

export function TripStatus({ status, testid }) {
  return <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TRIP_STATUS_STYLE[status] || ""}`}>{status}</span>;
}

export function expiryState(date) {
  const n = daysUntil(date);
  if (n === null) return "missing";
  return n < 0 ? "expired" : n <= 7 ? "expiring" : "ok";
}

const EXP_STYLE = {
  expired: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800",
  expiring: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  missing: "bg-secondary text-muted-foreground border-border",
};

export function ExpiryBadge({ date, testid }) {
  const s = expiryState(date);
  const n = daysUntil(date);
  const text = s === "missing" ? "No expiry" : s === "expired" ? `Expired ${fmtDate(date)}` : s === "expiring" ? `Expires in ${n}d` : `Valid till ${fmtDate(date)}`;
  return <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${EXP_STYLE[s]}`}>{text}</span>;
}

export function DocUpload({ label, value, onChange, refType = "market_doc", testid, required, disabled }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await uploadFile(`/uploads?ref_type=${refType}`, file);
      onChange(data.id);
      toast.success(`${label || "Document"} uploaded — check it with the eye icon`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const icon = "rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40";
  const remove = () => { if (window.confirm(`Remove ${label || "this"} file?${required ? " A new file is required before saving." : ""}`)) onChange(null); };
  return (
    <div className="flex flex-wrap items-center gap-1">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} />
      {!value ? (
        <button type="button" data-testid={testid} disabled={busy || disabled} onClick={() => ref.current.click()}
          className={`flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-xs font-semibold transition-colors hover:border-primary hover:text-foreground disabled:opacity-40 ${required ? "border-rose-300 text-muted-foreground" : "text-muted-foreground"}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload {label}{required ? " *" : ""}
        </button>
      ) : (
        <>
          <span className="flex items-center gap-1 rounded-lg border border-emerald-400 px-2 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400" data-testid={`${testid}-done`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}{label || "Uploaded"}</span>
          <button type="button" title="View document" data-testid={`${testid}-view`} className={icon} onClick={() => setView(true)}><Eye className="h-4 w-4" /></button>
          {!disabled && <button type="button" title="Edit / Replace document" data-testid={testid} disabled={busy} className={icon} onClick={() => ref.current.click()}><Pencil className="h-4 w-4" /></button>}
          {!disabled && <button type="button" title="Remove file" data-testid={`${testid}-remove`} disabled={busy} className={`${icon} hover:text-rose-600`} onClick={remove}><Trash2 className="h-4 w-4" /></button>}
        </>
      )}
      {view && <DocPreview fileId={value} title={label} onClose={() => setView(false)} />}
    </div>
  );
}

export function downloadCsv(filename, header, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [header, ...rows];
  const blob = new Blob(["\uFEFF" + lines.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

export const nativeInput = "native-select w-full";

export const waPhone = (p) => { const d = String(p || "").replace(/\D/g, ""); return d.length === 10 ? `91${d}` : d; };

export function WhatsAppPdfShare({ info, testid = "vendor-share" }) {
  const [busy, setBusy] = useState(false);
  const getFile = async () => {
    const r = await fetch(info.pdf_url);
    if (!r.ok) throw new Error("PDF not available");
    return new File([await r.blob()], info.file_name, { type: "application/pdf" });
  };
  const sharePdf = async () => {
    setBusy(true);
    try {
      const file = await getFile();
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: info.message });
      else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file);
        a.download = info.file_name;
        a.click();
        toast.info("PDF downloaded — attach it in WhatsApp");
      }
    } catch (e) { if (e.name !== "AbortError") toast.error(e.message || "Share failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="max-h-48 overflow-y-auto whitespace-pre-line break-all rounded-lg border bg-secondary/50 p-3 text-xs" data-testid={`${testid}-preview`}>{info.message}</div>
      <a data-testid={`${testid}-whatsapp-btn`} href={`https://wa.me/${waPhone(info.phone)}?text=${encodeURIComponent(info.message)}`} target="_blank" rel="noreferrer"
        className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
        <MessageCircle className="h-4 w-4" /> WhatsApp to {info.phone} (message + PDF link)
      </a>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid={`${testid}-pdf-share-btn`} disabled={busy} onClick={sharePdf}
          className="flex h-10 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-colors hover:bg-secondary disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share PDF File
        </button>
        <a data-testid={`${testid}-pdf-open-btn`} href={info.pdf_url} target="_blank" rel="noreferrer"
          className="flex h-10 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-colors hover:bg-secondary">
          <Printer className="h-4 w-4" /> Print / Open PDF
        </a>
      </div>
      <p className="text-[11px] text-muted-foreground">On mobile, "Share PDF File" sends the actual PDF to WhatsApp.</p>
    </div>
  );
}
